// The agent's server-side widget configuration: its shape, and the loader
// that fetches it.
//
// Split out of element.ts because it is a small state machine, not a getter.
// It carries five pieces of interlocking state - the config, whether a fetch
// is in flight, whether one has ever succeeded, why the last one failed, and
// which agent generation the answer belongs to - and every one of them has a
// reason to exist that is easy to undo by accident. Two shipped bugs came
// from exactly that: a late reply for a previous agent overwriting a new
// agent's config, and a config that resolved after telemetry had already
// released. Kept together here, the invariants are visible in one place and
// testable without a DOM.

import { fetchWithTimeout, resolveApiBase } from "./api-base.js";

/**
 * Mirrors the Go `WidgetConfig` struct served by
 * GET /v1/agents/{id}/widget-config. Only the subset the widget consumes is
 * typed; unknown fields are ignored so the server can ship additive fields
 * without breaking bundles already running on customer pages.
 */
export interface ServerWidgetConfig {
  version?: number;
  style?: "pill" | "fab";
  theme?: "dark" | "light" | "auto";
  avatar?: {
    type?: "orb" | "image";
    image_url?: string;
    orb_color_1?: string;
    orb_color_2?: string;
  };
  text?: {
    start_call?: string;
    end_call?: string;
    listening?: string;
    thinking?: string;
    speaking?: string;
  };
  terms?: {
    enabled?: boolean;
    content?: string;
  };
  transcript?: {
    enabled?: boolean;
  };
  telemetry?: {
    enabled?: boolean;
  };
}

export interface ConfigLoaderCallbacks {
  /** A fresh config was applied. Fires once per successful load. */
  onLoaded(config: ServerWidgetConfig): void;
  /**
   * The config could not be read: the request failed, or the server answered
   * something other than 2xx. `status` is the HTTP status when there was one.
   * Fires on every failed attempt, so it must be idempotent.
   */
  onUnavailable(status: number | undefined): void;
  /** Integration warning, shown only when the embed carries `debug`. */
  onWarn(...args: unknown[]): void;
}

export class WidgetConfigLoader {
  readonly #callbacks: ConfigLoaderCallbacks;

  #config: ServerWidgetConfig | null = null;
  /** Guards against overlapping fetches for the same agent. */
  #inFlight: Promise<void> | null = null;
  /**
   * True only once a fetch has returned a usable object. The terms gate
   * depends on this: with no successful load the widget cannot tell "no
   * terms configured" from "we could not ask", so it must fail closed rather
   * than read the absence of a gate as the absence of a requirement.
   */
  #loaded = false;
  /** HTTP status of the last failed attempt, so a failure can be reported by its actual cause. */
  #failureStatus: number | undefined;
  /**
   * Bumped whenever the agent changes. A reply that belongs to a superseded
   * request is inert: without this an SPA that swaps `agent-id` applied agent
   * A's terms gate and appearance to a session minted for agent B.
   */
  #generation = 0;

  constructor(callbacks: ConfigLoaderCallbacks) {
    this.#callbacks = callbacks;
  }

  get config(): ServerWidgetConfig | null {
    return this.#config;
  }

  /** True once a fetch has returned a usable object. */
  get loaded(): boolean {
    return this.#loaded;
  }

  get failureStatus(): number | undefined {
    return this.#failureStatus;
  }

  /**
   * Drops everything known about the previous agent and supersedes any
   * outstanding request. Call this when `agent-id` changes - clearing the
   * in-flight guard alone would start a second request while the first is
   * still outstanding, and the late reply would then overwrite the new
   * agent's config.
   */
  invalidate(): void {
    this.#config = null;
    this.#loaded = false;
    this.#failureStatus = undefined;
    this.#inFlight = null;
    this.#generation += 1;
  }

  /**
   * Fetches the config for `agentId`, or returns the outstanding request when
   * one is already running. Never refetches after a success, and resolves
   * immediately when there is no agent to fetch for.
   *
   * Safe to call repeatedly: the in-flight guard makes duplicate calls free,
   * which is what lets both the reveal path and the telemetry path ask for it
   * without coordinating.
   */
  load(agentId: string | null, apiBaseAttr: string | null): Promise<void> {
    if (this.#loaded) return Promise.resolve();
    if (this.#inFlight) return this.#inFlight;
    if (!agentId) return Promise.resolve();

    // Resolved through the shared helper so this path honours the build-time
    // origin exactly like the session path. Hardcoding production here meant
    // a non-production build minted sessions against one origin and read its
    // config from another, where the agent does not exist.
    const url = `${resolveApiBase(apiBaseAttr)}/v1/agents/${encodeURIComponent(agentId)}/widget-config`;
    const generation = this.#generation;
    const superseded = () => generation !== this.#generation;

    this.#inFlight = (async () => {
      try {
        // No `cache: "no-store"`: the handler serves this with max-age=60 +
        // stale-while-revalidate specifically so edge and browser caches
        // absorb the per-page-load fetch, and bypassing it put an uncached
        // origin round trip on every single page view.
        const r = await fetchWithTimeout(url, { method: "GET", credentials: "omit" });
        if (superseded()) return;
        if (!r.ok) {
          this.#failureStatus = r.status;
          this.#callbacks.onUnavailable(r.status);
          // 401/403 is the expected anonymous-rejected path on a private
          // agent; suppress so integrators with intentionally-private agents
          // do not see a confusing warning.
          if (r.status !== 401 && r.status !== 403) {
            this.#callbacks.onWarn(`<speechify-agent> widget-config fetch failed (${r.status})`);
          }
          return;
        }
        const body: unknown = await r.json();
        if (superseded()) return;
        // A 200 with a non-object body (null, array, primitive) is a server
        // bug, not a transient failure, so it settles as an empty config
        // rather than retrying forever.
        this.#config = body && typeof body === "object" && !Array.isArray(body) ? (body as ServerWidgetConfig) : {};
        this.#loaded = true;
        this.#failureStatus = undefined;
        this.#callbacks.onLoaded(this.#config);
      } catch (err) {
        if (superseded()) return;
        this.#callbacks.onUnavailable(undefined);
        this.#callbacks.onWarn("<speechify-agent> widget-config fetch error", err);
      } finally {
        // Only the current generation owns the guard; a superseded request
        // clearing it would strand the live one behind a stale null.
        if (!superseded()) this.#inFlight = null;
      }
    })();
    return this.#inFlight;
  }
}
